import { api } from '../../api/client';
import type { SedrCategory, SedrDashboardMetrics, SedrRequirement } from '../../api/sedr';
import { guestApi } from '../../api/guest';
import { sedrApi } from '../../api/sedr';
import { getAppState } from '../../app';
import { applyI18n, t } from '../../i18n';
import { fileTypeIconFor, fileTypeLabelKeyFor } from '../../files/fileTypes';
import { showMessageDialog } from '../../ui/confirmDialog';
import { fileTypeImg } from '../../ui/fileTypeImg';
import { iconImg } from '../../ui/icons';
import { sedrErrorMessageKey } from './sedrErrors';

export function formatSedrDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

export function formatPhaseDateRange(phase: { startAt: string | null; endAt: string | null }): string {
  const start = phase.startAt ? formatSedrDate(phase.startAt) : null;
  const end = phase.endAt ? formatSedrDate(phase.endAt) : null;
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  if (end) return end;
  return '—';
}

export function formatSedrDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function statusBadgeClass(status: string): string {
  switch (status) {
    case 'PendingUpload':
      return 'status-badge status-badge--danger';
    case 'Denied':
      return 'status-badge status-badge--danger';
    case 'UnderReview':
      return 'status-badge status-badge--warning';
    case 'ApprovedDischarged':
      return 'status-badge status-badge--success';
    case 'NotApplicable':
    case 'Superseded':
    case 'Retired':
    case 'Archived':
      return 'status-badge status-badge--muted';
    default:
      return 'status-badge';
  }
}

export function tSedrStatus(status: string): string {
  const key = `sedr.status.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

export function isSedrApprovalDividerComment(comment: {
  causesStatusChange?: boolean;
  resultingStatus?: string | null;
  declinesLifecycleChange?: boolean;
}): boolean {
  return Boolean(
    comment.causesStatusChange
    && !comment.declinesLifecycleChange
    && comment.resultingStatus === 'ApprovedDischarged',
  );
}

export function formatSedrCommentStatusFlag(comment: {
  causesStatusChange?: boolean;
  resultingStatus?: string | null;
  declinesLifecycleChange?: boolean;
  relatedRequirementCode?: string | null;
}): string | null {
  if (comment.declinesLifecycleChange) {
    const status = tSedrStatus(comment.resultingStatus ?? '');
    if (comment.relatedRequirementCode) {
      return t('sedr.drawer.lifecycleDeclineCommentSuperseded', {
        status,
        code: comment.relatedRequirementCode,
      });
    }
    return t('sedr.drawer.lifecycleDeclineComment', { status });
  }
  if (isSedrApprovalDividerComment(comment)) {
    return t('sedr.drawer.approvalDivider');
  }
  if (comment.causesStatusChange) {
    return t('sedr.drawer.statusChangeComment', {
      status: tSedrStatus(comment.resultingStatus ?? 'Denied'),
    });
  }
  return null;
}

export function tSedrLifecycle(state: string): string {
  const key = `sedr.lifecycle.${state}`;
  const translated = t(key);
  return translated === key ? state : translated;
}

export function tSedrCategory(category: string, categories?: SedrCategory[]): string {
  const list = categories ?? getCachedSedrCategories();
  const fromList = list.find((c) => c.id === category)?.name;
  if (fromList) return fromList;
  const key = `sedr.category.${category}`;
  const translated = t(key);
  if (!translated || translated === key) return category;
  return translated;
}

/** Prefer API-resolved name, then cache/list lookup, then legacy i18n / raw id. */
export function displaySedrCategory(
  category: string,
  categoryName?: string | null,
  categories?: SedrCategory[],
): string {
  if (categoryName) return categoryName;
  return tSedrCategory(category, categories);
}

let cachedSedrCategoriesRoomId: string | null = null;
let cachedSedrCategories: SedrCategory[] | null = null;

export type SedrGuestCategoryContext = {
  roomToken?: string;
  guestInviteId?: string;
  accountRoomId?: string;
};

export async function loadSedrCategories(
  roomId: string,
  force = false,
  guest?: SedrGuestCategoryContext,
): Promise<SedrCategory[]> {
  if (!force && cachedSedrCategories && cachedSedrCategoriesRoomId === roomId) {
    return cachedSedrCategories;
  }
  if (guest?.accountRoomId) {
    cachedSedrCategories = await guestApi.listCategories(guest.accountRoomId);
  } else if (guest?.roomToken && guest?.guestInviteId) {
    cachedSedrCategories = await sedrApi.guestListCategories(guest.roomToken, guest.guestInviteId);
  } else {
    cachedSedrCategories = await sedrApi.listCategories(roomId);
  }
  cachedSedrCategoriesRoomId = roomId;
  return cachedSedrCategories;
}

export function seedSedrCategoriesCache(roomId: string, categories: SedrCategory[]): void {
  cachedSedrCategories = categories;
  cachedSedrCategoriesRoomId = roomId;
}

export function getCachedSedrCategories(): SedrCategory[] {
  return cachedSedrCategories ?? [];
}

export function invalidateSedrCategoriesCache(roomId?: string): void {
  if (roomId && cachedSedrCategoriesRoomId && cachedSedrCategoriesRoomId !== roomId) {
    return;
  }
  cachedSedrCategories = null;
  cachedSedrCategoriesRoomId = null;
}

export function tSedrAuditAction(action: string): string {
  const key = `sedr.audit.action.${action}`;
  const translated = t(key);
  return translated === key ? action : translated;
}

function parseAuditFileDisplay(details: Record<string, unknown>): {
  name: string;
  path: string | null;
  contentType: string | null;
} | null {
  const name = typeof details.name === 'string' && details.name
    ? details.name
    : typeof details.fileName === 'string' && details.fileName
      ? details.fileName
      : null;
  if (!name) {
    return null;
  }

  const path = typeof details.path === 'string' && details.path ? details.path : null;
  const contentType = typeof details.contentType === 'string' ? details.contentType : null;
  return { name, path, contentType };
}

export function formatSedrAuditDocument(detailsJson: string | null): string {
  if (!detailsJson) {
    return '—';
  }

  try {
    const details = JSON.parse(detailsJson) as Record<string, unknown>;
    const file = parseAuditFileDisplay(details);
    if (file) {
      if (typeof details.requirementCode === 'string' && details.requirementCode) {
        const label = file.path ?? file.name;
        return `${details.requirementCode} · ${label}`;
      }
      return file.path ?? file.name;
    }

    if (typeof details.requirementCode === 'string' && details.requirementCode) {
      if (typeof details.summary === 'string' && details.summary) {
        return `${details.requirementCode} · ${details.summary}`;
      }

      const lifecycle = typeof details.requestedLifecycleStatus === 'string'
        ? details.requestedLifecycleStatus
        : null;
      if (lifecycle) {
        const statusLabel = tSedrStatus(lifecycle);
        if (lifecycle === 'Superseded'
          && typeof details.supersededByRequirementCode === 'string'
          && details.supersededByRequirementCode) {
          const supersedeCode = details.supersededByRequirementCode;
          const supersedeLabel = t('sedr.audit.summary.superseded', {
            status: statusLabel,
            code: supersedeCode,
          });
          const supersedeText = !supersedeLabel || supersedeLabel === 'sedr.audit.summary.superseded'
            ? `${statusLabel} → ${supersedeCode}`
            : supersedeLabel;
          return `${details.requirementCode} · ${supersedeText}`;
        }
        return `${details.requirementCode} · ${statusLabel}`;
      }

      const changes = Array.isArray(details.changes) ? details.changes : null;
      if (changes && changes.length > 0) {
        if (changes.length === 1) {
          const field = typeof (changes[0] as { field?: unknown })?.field === 'string'
            ? (changes[0] as { field: string }).field
            : 'field';
          const fieldKey = `sedr.audit.detail.${field}`;
          const fieldTranslated = t(fieldKey);
          const fieldLabel = !fieldTranslated || fieldTranslated === fieldKey ? field : fieldTranslated;
          const oneField = t('sedr.audit.summary.oneField', { field: fieldLabel });
          const oneFieldText = !oneField || oneField === 'sedr.audit.summary.oneField'
            ? fieldLabel
            : oneField;
          return `${details.requirementCode} · ${oneFieldText}`;
        }
        const countLabel = t('sedr.audit.summary.fieldCount', { count: changes.length });
        const countText = !countLabel || countLabel === 'sedr.audit.summary.fieldCount'
          ? `${changes.length} fields`
          : countLabel;
        return `${details.requirementCode} · ${countText}`;
      }

      if (typeof details.status === 'string' && details.status) {
        return `${details.requirementCode} · ${tSedrStatus(details.status)}`;
      }

      if (typeof details.title === 'string' && details.title) {
        return `${details.requirementCode} · ${details.title}`;
      }

      return details.requirementCode;
    }

    if (typeof details.displayName === 'string' && details.displayName) {
      if (typeof details.organization === 'string' && details.organization) {
        return `${details.displayName} (${details.organization})`;
      }
      if (typeof details.permission === 'string' && details.permission) {
        return `${details.displayName} (${details.permission})`;
      }
      return details.displayName;
    }

    if (typeof details.title === 'string' && details.title) {
      return details.title;
    }

    if (typeof details.created === 'number') {
      return t('sedr.audit.importSummary', {
        created: details.created,
        skipped: details.skipped ?? 0,
      });
    }

    if (typeof details.requirementsCreated === 'number') {
      return t('sedr.audit.seedSummary', {
        requirements: details.requirementsCreated,
        artifacts: details.artifactsLinked ?? 0,
      });
    }

    if (typeof details.purgeAt === 'string' && details.purgeAt) {
      return new Date(details.purgeAt).toLocaleString();
    }
  } catch {
    return '—';
  }

  return '—';
}

export function renderSedrAuditDocumentCell(detailsJson: string | null): HTMLElement {
  const cell = document.createElement('span');
  cell.className = 'sedr-audit-document';

  if (!detailsJson) {
    cell.textContent = '—';
    return cell;
  }

  try {
    const details = JSON.parse(detailsJson) as Record<string, unknown>;
    const file = parseAuditFileDisplay(details);
    if (file) {
      const label = document.createElement('span');
      label.className = 'sedr-audit-document__label';
      const displayPath = file.path ?? file.name;
      label.title = displayPath;
      label.append(
        fileTypeImg(fileTypeIconFor(file.name, file.contentType), 16),
        document.createTextNode(displayPath),
      );
      cell.append(label);
      return cell;
    }
  } catch {
    // fall through to text fallback
  }

  cell.textContent = formatSedrAuditDocument(detailsJson);
  return cell;
}

export type SedrMetricKind = 'total' | 'evidence' | 'pendingApproval' | 'approved';

export function renderMetricsRow(
  metrics: SedrDashboardMetrics,
  options?: { onSelect?: (metric: SedrMetricKind) => void },
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'sedr-metrics';
  const items: Array<{
    kind: SedrMetricKind;
    labelKey: string;
    value: number;
    accent: string;
  }> = [
    {
      kind: 'total',
      labelKey: 'sedr.metrics.totalRequirements',
      value: metrics.totalRequirements,
      accent: 'total',
    },
    {
      kind: 'evidence',
      labelKey: 'sedr.metrics.uploadedEvidence',
      value: metrics.uploadedEvidence,
      accent: 'evidence',
    },
    {
      kind: 'pendingApproval',
      labelKey: 'sedr.metrics.pendingApproval',
      value: metrics.pendingApproval,
      accent: 'pending',
    },
    {
      kind: 'approved',
      labelKey: 'sedr.metrics.approved',
      value: metrics.approved,
      accent: 'approved',
    },
  ];

  for (const item of items) {
    const card = options?.onSelect
      ? document.createElement('button')
      : document.createElement('div');
    if (card instanceof HTMLButtonElement) {
      card.type = 'button';
      card.addEventListener('click', () => options?.onSelect?.(item.kind));
    }
    card.className = `sedr-metric-card sedr-metric-card--${item.accent}`;
    card.innerHTML = `
      <span class="sedr-metric-card__value">${item.value}</span>
      <span class="sedr-metric-card__label" data-i18n="${item.labelKey}"></span>
    `;
    row.append(card);
  }

  applyI18n(row);
  return row;
}

export function isSedrRoomReadOnly(lifecycleState: string, archivedAt?: string | null): boolean {
  return lifecycleState === 'PendingDeletion' || Boolean(archivedAt);
}

export type SedrRoomHeaderModel = {
  title: string;
  clientOrganization: string;
  lifecycleState: string;
  purgeAt?: string | null;
  archivedAt?: string | null;
  deletionRequestedByName?: string | null;
  deletionReason?: string | null;
};

export type SedrHeaderBannerAction = {
  labelKey: string;
  className: string;
  onClick: () => void;
};

export type SedrRoomStatusBannerOptions = {
  actions?: SedrHeaderBannerAction[];
};

/** Compact status chip for the room header (does not consume main-page space). */
export function renderRoomStatusBanner(
  room: {
    lifecycleState: string;
    purgeAt?: string | null;
    archivedAt?: string | null;
    deletionRequestedByName?: string | null;
    deletionReason?: string | null;
  },
  options?: SedrRoomStatusBannerOptions,
): HTMLElement | null {
  const actions = options?.actions;

  if (room.archivedAt) {
    return createHeaderBanner({
      kind: 'archived',
      label: t('sedr.archive.banner'),
      detail: t('sedr.archive.readOnlyHint'),
      actions,
    });
  }

  if (room.lifecycleState === 'SetupIncomplete') {
    return createHeaderBanner({
      kind: 'setup',
      label: tSedrLifecycle('SetupIncomplete'),
      actions,
    });
  }

  if (room.lifecycleState === 'DeletionRequested') {
    const name = room.deletionRequestedByName;
    const reason = room.deletionReason?.trim();
    if (!name) {
      return createHeaderBanner({
        kind: 'requested',
        label: tSedrLifecycle('DeletionRequested'),
        deletionReason: reason,
        actions,
      });
    }
    return createHeaderBanner({
      kind: 'requested',
      label: tSedrLifecycle('DeletionRequested'),
      detail: t('sedr.deletion.requestedBy', { name, interpolation: { escapeValue: false } }),
      title: t('sedr.deletion.requestedBanner', { name, interpolation: { escapeValue: false } }),
      deletionReason: reason,
      actions,
    });
  }

  if (!isSedrRoomReadOnly(room.lifecycleState, room.archivedAt) || !room.purgeAt) {
    return null;
  }

  const unescape = { interpolation: { escapeValue: false } };
  const purgeLine = t('sedr.deletion.purgeOn', { date: formatSedrDate(room.purgeAt), ...unescape });

  return createHeaderBanner({
    kind: 'pending',
    label: tSedrLifecycle('PendingDeletion'),
    detail: purgeLine,
    title: t('sedr.deletion.readOnlyHint'),
    deletionReason: room.deletionReason?.trim(),
    actions,
  });
}

export function renderRoomHeader(host: HTMLElement, room: SedrRoomHeaderModel): void {
  const heading = document.createElement('h1');
  heading.append(document.createTextNode(room.title));
  if (room.clientOrganization) {
    const org = document.createElement('span');
    org.className = 'muted';
    org.textContent = ` · ${room.clientOrganization}`;
    heading.append(org);
  }
  host.replaceChildren(heading);
  const banner = renderRoomStatusBanner(room);
  if (banner) {
    host.append(banner);
  }
}

function createHeaderBanner(options: {
  kind: 'setup' | 'requested' | 'pending' | 'archived';
  label: string;
  detail?: string;
  title?: string;
  deletionReason?: string;
  actions?: SedrHeaderBannerAction[];
}): HTMLElement {
  const { kind, label, detail, title, deletionReason, actions } = options;
  const bar = document.createElement('aside');
  bar.className = `sedr-header-banner sedr-header-banner--${kind}`;
  bar.setAttribute('role', 'status');
  if (title) {
    bar.title = title;
  }

  const text = document.createElement('div');
  text.className = 'sedr-header-banner__text';

  const labelEl = document.createElement('strong');
  labelEl.className = 'sedr-header-banner__label';
  labelEl.textContent = label;
  text.append(labelEl);

  if (detail) {
    const detailEl = document.createElement('span');
    detailEl.className = 'sedr-header-banner__detail';
    detailEl.textContent = detail;
    text.append(detailEl);
  }

  bar.append(text);

  if (actions && actions.length > 0) {
    const actionsEl = document.createElement('div');
    actionsEl.className = 'sedr-header-banner__actions';
    for (const action of actions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = action.className;
      btn.setAttribute('data-i18n', action.labelKey);
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        action.onClick();
      });
      actionsEl.append(btn);
    }
    applyI18n(actionsEl);
    bar.append(actionsEl);
  }

  if (deletionReason) {
    makeDeletionReasonBannerInteractive(text, deletionReason, title);
    bar.classList.add('sedr-header-banner--interactive');
  }

  return bar;
}

function makeDeletionReasonBannerInteractive(
  target: HTMLElement,
  reason: string,
  statusTitle?: string,
): void {
  target.classList.add('sedr-header-banner__text--interactive');
  target.setAttribute('role', 'button');
  target.tabIndex = 0;
  const hint = t('sedr.deletion.viewReasonHint');
  target.title = statusTitle ? `${statusTitle}\n${hint}` : hint;
  target.setAttribute('aria-label', `${target.textContent?.trim() ?? ''} — ${hint}`);

  const openReason = () => {
    void showMessageDialog({
      titleKey: 'sedr.deletion.reasonTitle',
      message: reason,
    });
  };

  target.addEventListener('click', openReason);
  target.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openReason();
    }
  });
}

export function renderSetupGate(options: {
  onInviteAdmin: (userId: string) => void | Promise<void>;
  onDeleteRoom: () => void | Promise<void>;
}): HTMLElement {
  const gate = document.createElement('div');
  gate.className = 'sedr-setup-gate';
  gate.innerHTML = `
    <h3 data-i18n="sedr.setup.title"></h3>
    <p class="muted" data-i18n="sedr.setup.subtitle"></p>
    <form class="form-grid sedr-setup-form" id="sedr-setup-invite-form">
      <label>
        <span data-i18n="sedr.setup.adminUserLabel"></span>
        <select name="adminUserId" required>
          <option value="" data-i18n="sedr.setup.adminUserPlaceholder"></option>
        </select>
      </label>
      <div class="sedr-setup-actions">
        <button type="submit" class="button primary" id="sedr-invite-admin" data-i18n="sedr.setup.inviteAdmin"></button>
        <button type="button" class="button subtle" id="sedr-delete-room" data-i18n="sedr.deletion.deleteNow"></button>
      </div>
      <p class="error-text" id="sedr-setup-error" hidden></p>
    </form>
  `;
  applyI18n(gate);

  const form = gate.querySelector('#sedr-setup-invite-form') as HTMLFormElement;
  const select = form.querySelector('select[name="adminUserId"]') as HTMLSelectElement;
  const errorEl = gate.querySelector('#sedr-setup-error') as HTMLParagraphElement;
  const inviteBtn = gate.querySelector('#sedr-invite-admin') as HTMLButtonElement;

  void (async () => {
    try {
      const state = getAppState();
      const currentUserId = state.kind === 'authenticated' ? state.session.userId : '';
      const directory = await api.getKeyDirectory();
      const candidates = directory.filter((e) => e.userId !== currentUserId);
      for (const entry of candidates) {
        const option = document.createElement('option');
        option.value = entry.userId;
        option.textContent = entry.displayName;
        select.append(option);
      }
      if (candidates.length === 0) {
        inviteBtn.disabled = true;
        select.disabled = true;
      }
    } catch {
      inviteBtn.disabled = true;
      select.disabled = true;
      errorEl.hidden = false;
      errorEl.textContent = t('status.error');
    }
    applyI18n(select);
  })();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const userId = String(new FormData(form).get('adminUserId') ?? '').trim();
    if (!userId) return;
    void (async () => {
      inviteBtn.disabled = true;
      errorEl.hidden = true;
      try {
        await options.onInviteAdmin(userId);
      } catch (cause) {
        inviteBtn.disabled = false;
        errorEl.hidden = false;
        errorEl.textContent = t(sedrErrorMessageKey(cause));
      }
    })();
  });

  gate.querySelector('#sedr-delete-room')?.addEventListener('click', () => {
    void options.onDeleteRoom();
  });

  return gate;
}

export type SedrRequirementStatus =
  | 'PendingUpload'
  | 'UnderReview'
  | 'ApprovedDischarged'
  | 'Denied'
  | 'NotApplicable'
  | 'Superseded'
  | 'Retired'
  | 'Archived';

/** Empty array means “none” (no statuses selected). Prefer {@link sedrAllActiveStatusFilter} for the default view. */
export type SedrStatusFilter = SedrRequirementStatus[];

export type SedrStatusQuickLink = 'all' | 'allActive' | 'none';

export const SEDR_REQUIREMENT_STATUSES: readonly SedrRequirementStatus[] = [
  'PendingUpload',
  'UnderReview',
  'Denied',
  'ApprovedDischarged',
  'NotApplicable',
  'Superseded',
  'Retired',
  'Archived',
] as const;

export const SEDR_LIFECYCLE_STATUSES: readonly SedrRequirementStatus[] = [
  'NotApplicable',
  'Superseded',
  'Retired',
  'Archived',
] as const;

export type SedrLifecycleStatus = (typeof SEDR_LIFECYCLE_STATUSES)[number];

export const SEDR_ACTIVE_SUPERSEDE_STATUSES: readonly SedrRequirementStatus[] = [
  'PendingUpload',
  'UnderReview',
  'Denied',
  'ApprovedDischarged',
] as const;

export function sedrAllActiveStatusFilter(): SedrStatusFilter {
  return [...SEDR_ACTIVE_SUPERSEDE_STATUSES];
}

export function sedrAllStatusFilter(): SedrStatusFilter {
  return [...SEDR_REQUIREMENT_STATUSES];
}

export function isSedrLifecycleClosed(status: string): boolean {
  return (SEDR_LIFECYCLE_STATUSES as readonly string[]).includes(status);
}

export function isActiveForSupersedeTarget(status: string): boolean {
  return (SEDR_ACTIVE_SUPERSEDE_STATUSES as readonly string[]).includes(status);
}

function sameStatusSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((s) => set.has(s));
}

export function detectSedrStatusQuickLink(filter: SedrStatusFilter): SedrStatusQuickLink | null {
  if (filter.length === 0) return 'none';
  if (sameStatusSet(filter, SEDR_REQUIREMENT_STATUSES)) return 'all';
  if (sameStatusSet(filter, SEDR_ACTIVE_SUPERSEDE_STATUSES)) return 'allActive';
  return null;
}

export function applySedrStatusQuickLink(link: SedrStatusQuickLink): SedrStatusFilter {
  if (link === 'all') return sedrAllStatusFilter();
  if (link === 'allActive') return sedrAllActiveStatusFilter();
  return [];
}

export function statusFilterIncludesObsolete(filter: SedrStatusFilter): boolean {
  return filter.some((s) => isSedrLifecycleClosed(s));
}

/** Derived hideObsolete for export defaults: true when no lifecycle statuses are selected. */
export function statusFilterHidesObsolete(filter: SedrStatusFilter): boolean {
  return !statusFilterIncludesObsolete(filter);
}

export function selectedObsoleteStatuses(filter: SedrStatusFilter): SedrRequirementStatus[] {
  return SEDR_LIFECYCLE_STATUSES.filter((s) => filter.includes(s));
}

export function toggleObsoleteInStatusFilter(
  current: SedrStatusFilter,
  subtypes: readonly SedrRequirementStatus[],
): SedrStatusFilter {
  const active = current.filter((s) => !isSedrLifecycleClosed(s));
  if (statusFilterIncludesObsolete(current)) return active;
  const allowed = new Set(subtypes);
  return [...active, ...SEDR_LIFECYCLE_STATUSES.filter((s) => allowed.has(s))];
}

export function setObsoleteSubtypesInStatusFilter(
  current: SedrStatusFilter,
  subtypes: readonly SedrRequirementStatus[],
): SedrStatusFilter {
  const active = current.filter((s) => !isSedrLifecycleClosed(s));
  const allowed = new Set(subtypes);
  return [...active, ...SEDR_LIFECYCLE_STATUSES.filter((s) => allowed.has(s))];
}

/** Label for the Obsolete chip from the statuses currently shown. */
export function obsoleteFilterChipLabel(shown: readonly SedrRequirementStatus[]): string {
  if (shown.length === 0 || shown.length >= SEDR_LIFECYCLE_STATUSES.length) {
    const label = t('sedr.filters.obsolete');
    return label === 'sedr.filters.obsolete' || !label ? 'Obsolete' : label;
  }
  return shown
    .map((s) => {
      const label = tSedrStatus(s);
      return label || s;
    })
    .join(', ');
}

/** Links may be added/removed only while both ends are still unapproved. */
export function canMutateRequirementLinks(status: string): boolean {
  return status === 'PendingUpload' || status === 'UnderReview' || status === 'Denied';
}

const BUILTIN_LINK_TYPES = [
  'DependsOn',
  'RelatedTo',
  'SharesEvidenceWith',
  'ChildOf',
  'ConflictsWith',
] as const;

export function isBuiltInRequirementLinkType(linkType: string): boolean {
  return (BUILTIN_LINK_TYPES as readonly string[]).includes(linkType);
}

/** Prefer i18n for builtins; fall back to API-resolved name. */
export function tSedrLinkTypeName(linkType: string, linkTypeName: string, inverse = false): string {
  if (isBuiltInRequirementLinkType(linkType)) {
    const key = inverse ? `sedr.linkType.${linkType}.inverse` : `sedr.linkType.${linkType}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return linkTypeName || linkType;
}

export type SedrCategoryFilter = 'all' | string;

/** Owner name, or special sentinels. */
export type SedrOwnerFilter = 'all' | 'unassigned' | string;

export type SedrRenewalFilter =
  | 'all'
  | 'renewalPending'
  | 'inGrace'
  | 'expiresSoon'
  | 'expired';

export type SedrLifecycleRequestFilter = 'all' | 'lifecyclePending';

export type SedrEvidenceFilter = 'all' | 'hasEvidence' | 'noEvidence';

export type SedrComplianceSortKey =
  | 'requirementCode'
  | 'title'
  | 'category'
  | 'phaseName'
  | 'status'
  | 'approvedByDisplay'
  | 'ownerName';

export type SedrComplianceSort = {
  key: SedrComplianceSortKey;
  dir: 'asc' | 'desc';
};

export const SEDR_COMPLIANCE_SORT_KEY = 'shareweb:sedr-compliance-sort';
export const SEDR_FILTERS_COLLAPSED_KEY = 'shareweb:sedr-filters-collapsed';
export const SEDR_EXPIRY_SOON_DAYS = 30;

const STATUS_SORT_ORDER: readonly string[] = [
  'PendingUpload',
  'UnderReview',
  'Denied',
  'ApprovedDischarged',
  'NotApplicable',
  'Superseded',
  'Retired',
  'Archived',
];

export function readFiltersCollapsed(): boolean {
  try {
    return localStorage.getItem(SEDR_FILTERS_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeFiltersCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SEDR_FILTERS_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function readComplianceSort(): SedrComplianceSort {
  try {
    const raw = localStorage.getItem(SEDR_COMPLIANCE_SORT_KEY);
    if (!raw) return { key: 'requirementCode', dir: 'asc' };
    const parsed = JSON.parse(raw) as Partial<SedrComplianceSort>;
    if (
      parsed
      && typeof parsed.key === 'string'
      && ['requirementCode', 'title', 'category', 'phaseName', 'status', 'approvedByDisplay', 'ownerName'].includes(parsed.key)
      && (parsed.dir === 'asc' || parsed.dir === 'desc')
    ) {
      return { key: parsed.key as SedrComplianceSortKey, dir: parsed.dir };
    }
  } catch {
    // ignore
  }
  return { key: 'requirementCode', dir: 'asc' };
}

export function writeComplianceSort(sort: SedrComplianceSort): void {
  try {
    localStorage.setItem(SEDR_COMPLIANCE_SORT_KEY, JSON.stringify(sort));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function requirementHasEvidence(req: SedrRequirement): boolean {
  if (req.linkedFileId) return true;
  return (req.artifacts ?? []).some((a) => !a.archivedAt);
}

export function collectRequirementOwners(items: SedrRequirement[]): string[] {
  const names = new Set<string>();
  for (const item of items) {
    const name = item.ownerName?.trim();
    if (name) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function compareNullableText(a: string | null | undefined, b: string | null | undefined): number {
  const left = (a ?? '').trim();
  const right = (b ?? '').trim();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true });
}

function statusSortIndex(status: string): number {
  const idx = STATUS_SORT_ORDER.indexOf(status);
  return idx >= 0 ? idx : STATUS_SORT_ORDER.length;
}

export function compareRequirements(
  a: SedrRequirement,
  b: SedrRequirement,
  sort: SedrComplianceSort,
): number {
  let cmp = 0;
  switch (sort.key) {
    case 'requirementCode':
      cmp = compareNullableText(a.requirementCode, b.requirementCode);
      break;
    case 'title':
      cmp = compareNullableText(a.title, b.title);
      break;
    case 'category':
      cmp = compareNullableText(
        displaySedrCategory(a.category, a.categoryName),
        displaySedrCategory(b.category, b.categoryName),
      );
      break;
    case 'phaseName':
      cmp = compareNullableText(a.phaseName, b.phaseName);
      break;
    case 'status':
      cmp = statusSortIndex(a.status) - statusSortIndex(b.status);
      if (cmp === 0) cmp = compareNullableText(tSedrStatus(a.status), tSedrStatus(b.status));
      break;
    case 'approvedByDisplay':
      cmp = compareNullableText(
        a.status === 'ApprovedDischarged' ? a.approvedByDisplay : null,
        b.status === 'ApprovedDischarged' ? b.approvedByDisplay : null,
      );
      break;
    case 'ownerName':
      cmp = compareNullableText(a.ownerName, b.ownerName);
      break;
    default:
      cmp = 0;
  }
  if (cmp === 0) {
    cmp = compareNullableText(a.requirementCode, b.requirementCode);
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

export function sortRequirements(
  items: SedrRequirement[],
  sort: SedrComplianceSort,
): SedrRequirement[] {
  return [...items].sort((a, b) => compareRequirements(a, b, sort));
}

export function countActiveComplianceFilters(options: {
  statusFilter: SedrStatusFilter;
  categoryFilter: SedrCategoryFilter;
  phaseId: string | null | 'unassigned';
  ownerFilter: SedrOwnerFilter;
  renewalFilter: SedrRenewalFilter;
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  evidenceFilter: SedrEvidenceFilter;
}): number {
  let count = 0;
  if (detectSedrStatusQuickLink(options.statusFilter) !== 'allActive') count += 1;
  if (options.categoryFilter !== 'all') count += 1;
  if (options.phaseId !== null) count += 1;
  if (options.ownerFilter !== 'all') count += 1;
  if (options.renewalFilter !== 'all') count += 1;
  if (options.lifecycleRequestFilter !== 'all') count += 1;
  if (options.evidenceFilter !== 'all') count += 1;
  return count;
}

const DISCHARGE_FILTER_LABEL_KEYS: Partial<Record<SedrRequirementStatus, string>> = {
  PendingUpload: 'sedr.filters.pendingUpload',
  UnderReview: 'sedr.filters.underReview',
  Denied: 'sedr.filters.denied',
  ApprovedDischarged: 'sedr.filters.approved',
};

function tLabel(key: string, fallback: string): string {
  const label = t(key);
  return label && label !== key ? label : fallback;
}

export type SedrComplianceFilterSummaryPart = { field: string; value: string };

function summaryPart(fieldKey: string, fieldFallback: string, value: string): SedrComplianceFilterSummaryPart {
  return { field: tLabel(fieldKey, fieldFallback), value };
}

function formatStatusFilterSummary(statusFilter: SedrStatusFilter): string {
  const quick = detectSedrStatusQuickLink(statusFilter);
  if (quick === 'allActive') return tLabel('sedr.filters.allActive', 'All active');
  if (quick === 'all') return tLabel('sedr.filters.all', 'All');
  if (quick === 'none') return tLabel('sedr.filters.none', 'None');

  const parts: string[] = [];
  for (const status of SEDR_ACTIVE_SUPERSEDE_STATUSES) {
    if (!statusFilter.includes(status)) continue;
    const key = DISCHARGE_FILTER_LABEL_KEYS[status];
    if (!key) continue;
    parts.push(tLabel(key, status));
  }
  const lifecycle = selectedObsoleteStatuses(statusFilter);
  if (lifecycle.length > 0) {
    parts.push(obsoleteFilterChipLabel(lifecycle));
  }
  return parts.length > 0 ? parts.join(', ') : tLabel('sedr.filters.none', 'None');
}

/** Structured collapsed summary parts (field + value) for rich rendering. */
export function listActiveComplianceFilterSummaryParts(options: {
  statusFilter: SedrStatusFilter;
  categoryFilter: SedrCategoryFilter;
  categories: SedrCategory[];
  phaseId: string | null | 'unassigned';
  phases: Array<{ id: string; name: string }>;
  ownerFilter: SedrOwnerFilter;
  renewalFilter: SedrRenewalFilter;
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  evidenceFilter: SedrEvidenceFilter;
  search: string;
  commentSearch?: string;
}): SedrComplianceFilterSummaryPart[] {
  const parts: SedrComplianceFilterSummaryPart[] = [];

  const search = options.search.trim();
  if (search) {
    parts.push(summaryPart('sedr.filters.search', 'Search', `"${search}"`));
  }

  const commentSearch = (options.commentSearch ?? '').trim();
  if (commentSearch) {
    parts.push(
      summaryPart('sedr.filters.commentContent', 'Comment content', `"${commentSearch}"`),
    );
  }

  parts.push(
    summaryPart('sedr.filters.status', 'Status', formatStatusFilterSummary(options.statusFilter)),
  );

  if (options.phaseId !== null) {
    const phaseValue = options.phaseId === 'unassigned'
      ? tLabel('sedr.phases.unassigned', 'Unassigned')
      : (options.phases.find((p) => p.id === options.phaseId)?.name ?? options.phaseId);
    parts.push(summaryPart('sedr.phases.field', 'Phase', phaseValue));
  }

  if (options.categoryFilter !== 'all') {
    const category = options.categories.find((c) => c.id === options.categoryFilter);
    parts.push(
      summaryPart('sedr.filters.category', 'Category', category?.name ?? options.categoryFilter),
    );
  }

  if (options.ownerFilter !== 'all') {
    const ownerValue = options.ownerFilter === 'unassigned'
      ? tLabel('sedr.filters.ownerUnassigned', 'Unassigned')
      : options.ownerFilter;
    parts.push(summaryPart('sedr.filters.owner', 'Owner', ownerValue));
  }

  if (options.renewalFilter !== 'all') {
    const key = `sedr.filters.${options.renewalFilter}`;
    parts.push(
      summaryPart('sedr.filters.renewal', 'Renewal / expiry', tLabel(key, options.renewalFilter)),
    );
  }

  if (options.lifecycleRequestFilter !== 'all') {
    const key = `sedr.filters.${options.lifecycleRequestFilter}`;
    parts.push(
      summaryPart(
        'sedr.filters.lifecycleRequest',
        'Lifecycle request',
        tLabel(key, options.lifecycleRequestFilter),
      ),
    );
  }

  if (options.evidenceFilter !== 'all') {
    const key = `sedr.filters.${options.evidenceFilter}`;
    parts.push(
      summaryPart('sedr.filters.evidence', 'Evidence', tLabel(key, options.evidenceFilter)),
    );
  }

  return parts;
}

/** Human-readable collapsed summary of active compliance filters (and search). */
export function formatActiveComplianceFiltersSummary(options: {
  statusFilter: SedrStatusFilter;
  categoryFilter: SedrCategoryFilter;
  categories: SedrCategory[];
  phaseId: string | null | 'unassigned';
  phases: Array<{ id: string; name: string }>;
  ownerFilter: SedrOwnerFilter;
  renewalFilter: SedrRenewalFilter;
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  evidenceFilter: SedrEvidenceFilter;
  search: string;
  commentSearch?: string;
}): string {
  return listActiveComplianceFilterSummaryParts(options)
    .map((part) => `${part.field}: ${part.value}`)
    .join(' · ');
}

function renderComplianceFilterSummary(
  parts: SedrComplianceFilterSummaryPart[],
): HTMLElement {
  const summary = document.createElement('span');
  summary.className = 'sedr-compliance-filters__summary';
  summary.title = parts.map((part) => `${part.field}: ${part.value}`).join(' · ');

  parts.forEach((part, index) => {
    if (index > 0) {
      const sep = document.createElement('span');
      sep.className = 'sedr-compliance-filters__summary-sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = ' · ';
      summary.append(sep);
    }
    const item = document.createElement('span');
    item.className = 'sedr-compliance-filters__summary-item';
    const field = document.createElement('span');
    field.className = 'sedr-compliance-filters__summary-field';
    field.textContent = `${part.field}: `;
    const value = document.createElement('span');
    value.className = 'sedr-compliance-filters__summary-value';
    value.textContent = part.value;
    item.append(field, value);
    summary.append(item);
  });

  return summary;
}

export function isSedrRequirementStatus(value: string): value is SedrRequirementStatus {
  return (SEDR_REQUIREMENT_STATUSES as readonly string[]).includes(value);
}

/**
 * Toggle a discharge-status chip. Empty selection is “none”.
 * Lifecycle statuses are controlled by the Obsolete split button, not this helper.
 */
export function toggleSedrStatusFilter(
  current: SedrStatusFilter,
  clicked: SedrRequirementStatus,
): SedrStatusFilter {
  const set = new Set(current);
  if (set.has(clicked)) set.delete(clicked);
  else set.add(clicked);
  return SEDR_REQUIREMENT_STATUSES.filter((s) => set.has(s));
}

export function renderComplianceCategorySelect(options: {
  categoryFilter: SedrCategoryFilter;
  categories: SedrCategory[];
  onChange: (category: SedrCategoryFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__category';
  wrap.innerHTML = `
    <span data-i18n="sedr.filters.category"></span>
    <select id="sedr-compliance-category"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;
  const choices: Array<[SedrCategoryFilter, string]> = [
    ['all', 'sedr.filters.all'],
    ...options.categories.map((c) => [c.id, c.name] as [SedrCategoryFilter, string]),
  ];
  for (const [value, label] of choices) {
    const opt = document.createElement('option');
    opt.value = value;
    if (value === 'all') {
      opt.setAttribute('data-i18n', label);
    } else {
      opt.textContent = label;
    }
    select.append(opt);
  }
  select.value = options.categoryFilter;
  select.addEventListener('change', () => {
    options.onChange(select.value);
  });
  applyI18n(wrap);
  return wrap;
}

export function renderCategorySelectOptions(
  select: HTMLSelectElement,
  categories: SedrCategory[],
  selected?: string,
): void {
  select.replaceChildren();
  for (const category of categories) {
    const opt = document.createElement('option');
    opt.value = category.id;
    opt.textContent = category.name;
    if (selected === category.id) {
      opt.selected = true;
    }
    select.append(opt);
  }
}

function renderStatusChipGroup(options: {
  className: string;
  ariaLabelKey: string;
  chips: Array<[SedrRequirementStatus, string]>;
  active: SedrStatusFilter;
  onChange: (filter: SedrStatusFilter) => void;
}): HTMLElement {
  const filters = document.createElement('div');
  filters.className = options.className;
  filters.setAttribute('role', 'group');
  filters.setAttribute('data-i18n-aria-label', options.ariaLabelKey);

  for (const [value, labelKey] of options.chips) {
    const pressed = options.active.includes(value);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `sedr-filter-chip${pressed ? ' sedr-filter-chip--active' : ''}`;
    btn.dataset.filter = value;
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    btn.setAttribute('data-i18n', labelKey);
    btn.addEventListener('click', () => options.onChange(toggleSedrStatusFilter(options.active, value)));
    filters.append(btn);
  }

  applyI18n(filters);
  return filters;
}

function renderStatusQuickLinks(options: {
  active: SedrStatusFilter;
  onChange: (filter: SedrStatusFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sedr-filters sedr-filters--quick';
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('data-i18n-aria-label', 'sedr.filters.quickGroup');

  const current = detectSedrStatusQuickLink(options.active);
  const links: Array<[SedrStatusQuickLink, string]> = [
    ['all', 'sedr.filters.all'],
    ['allActive', 'sedr.filters.allActive'],
    ['none', 'sedr.filters.none'],
  ];
  for (const [value, labelKey] of links) {
    const btn = document.createElement('button');
    btn.type = 'button';
    const pressed = current === value;
    btn.className = `sedr-filter-quick${pressed ? ' sedr-filter-quick--active' : ''}`;
    btn.dataset.quick = value;
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    btn.setAttribute('data-i18n', labelKey);
    btn.addEventListener('click', () => options.onChange(applySedrStatusQuickLink(value)));
    wrap.append(btn);
  }
  applyI18n(wrap);
  return wrap;
}

function renderObsoleteStatusControl(options: {
  active: SedrStatusFilter;
  obsoleteSubtypes: readonly SedrRequirementStatus[];
  onChange: (filter: SedrStatusFilter) => void;
  onObsoleteSubtypesChange: (subtypes: SedrRequirementStatus[]) => void;
}): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sedr-obsolete-split';

  const shown = selectedObsoleteStatuses(options.active);
  let shownNow = [...shown];
  const pressed = shown.length > 0;
  // Remembered set used when turning Obsolete back on via the main button.
  const turnOnWith =
    options.obsoleteSubtypes.length > 0
      ? SEDR_LIFECYCLE_STATUSES.filter((s) => options.obsoleteSubtypes.includes(s))
      : [...SEDR_LIFECYCLE_STATUSES];

  if (pressed) wrap.classList.add('sedr-obsolete-split--active');

  const main = document.createElement('button');
  main.type = 'button';
  main.className = `sedr-filter-chip sedr-obsolete-split__main${pressed ? ' sedr-filter-chip--active' : ''}`;
  main.dataset.filter = 'obsolete';
  main.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  main.textContent = obsoleteFilterChipLabel(shown);
  main.addEventListener('click', () => {
    options.onChange(toggleObsoleteInStatusFilter(options.active, turnOnWith));
  });

  const menuId = 'sedr-obsolete-subtype-menu';
  const chevron = document.createElement('button');
  chevron.type = 'button';
  chevron.className = 'sedr-obsolete-split__chevron';
  chevron.setAttribute('aria-haspopup', 'true');
  chevron.setAttribute('aria-expanded', 'false');
  chevron.setAttribute('aria-controls', menuId);
  chevron.setAttribute('data-i18n-aria-label', 'sedr.filters.obsoleteTypes');
  chevron.append(iconImg('chevron-down', 12));

  const menu = document.createElement('div');
  menu.id = menuId;
  menu.className = 'sedr-obsolete-split__menu';
  menu.hidden = true;
  menu.setAttribute('role', 'group');
  menu.setAttribute('data-i18n-aria-label', 'sedr.filters.obsoleteTypes');

  const closeMenu = () => {
    menu.hidden = true;
    chevron.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onDocPointer, true);
  };
  const onDocPointer = (event: PointerEvent) => {
    if (!wrap.contains(event.target as Node)) closeMenu();
  };

  const syncMain = (nextShown: readonly SedrRequirementStatus[]) => {
    const active = nextShown.length > 0;
    main.textContent = obsoleteFilterChipLabel(nextShown);
    main.classList.toggle('sedr-filter-chip--active', active);
    main.setAttribute('aria-pressed', active ? 'true' : 'false');
    wrap.classList.toggle('sedr-obsolete-split--active', active);
  };

  for (const status of SEDR_LIFECYCLE_STATUSES) {
    const row = document.createElement('label');
    row.className = 'sedr-obsolete-split__option';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = shown.includes(status);
    input.addEventListener('change', () => {
      const nextShown = SEDR_LIFECYCLE_STATUSES.filter((s) => {
        if (s === status) return input.checked;
        return shownNow.includes(s);
      });
      shownNow = nextShown;
      syncMain(nextShown);
      options.onObsoleteSubtypesChange([...nextShown]);
    });
    const text = document.createElement('span');
    text.textContent = tSedrStatus(status);
    row.append(input, text);
    menu.append(row);
  }

  chevron.addEventListener('click', (event) => {
    event.stopPropagation();
    const open = menu.hidden;
    if (open) {
      menu.hidden = false;
      chevron.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', onDocPointer, true);
    } else {
      closeMenu();
    }
  });

  wrap.append(main, chevron, menu);
  applyI18n(wrap);
  return wrap;
}

export function renderComplianceStatusFilters(
  active: SedrStatusFilter,
  onChange: (filter: SedrStatusFilter) => void,
  options?: {
    obsoleteSubtypes?: readonly SedrRequirementStatus[];
    onObsoleteSubtypesChange?: (subtypes: SedrRequirementStatus[]) => void;
  },
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'sedr-compliance-filters__status-chips';
  row.setAttribute('role', 'group');
  row.setAttribute('data-i18n-aria-label', 'sedr.filters.statusGroup');

  row.append(renderStatusQuickLinks({ active, onChange }));

  const divider = document.createElement('span');
  divider.className = 'sedr-filters__divider';
  divider.setAttribute('aria-hidden', 'true');
  row.append(divider);

  row.append(
    renderStatusChipGroup({
      className: 'sedr-filters sedr-filters--discharge',
      ariaLabelKey: 'sedr.filters.statusGroup',
      chips: [
        ['PendingUpload', 'sedr.filters.pendingUpload'],
        ['UnderReview', 'sedr.filters.underReview'],
        ['Denied', 'sedr.filters.denied'],
        ['ApprovedDischarged', 'sedr.filters.approved'],
      ],
      active,
      onChange,
    }),
  );

  const obsoleteSubtypes = options?.obsoleteSubtypes ?? SEDR_LIFECYCLE_STATUSES;
  const onObsoleteSubtypesChange =
    options?.onObsoleteSubtypesChange ?? (() => undefined);
  row.append(
    renderObsoleteStatusControl({
      active,
      obsoleteSubtypes,
      onChange,
      onObsoleteSubtypesChange,
    }),
  );

  applyI18n(row);
  return row;
}

/** @deprecated Lifecycle chips are folded into the Obsolete split control. */
export function renderComplianceLifecycleFilters(
  active: SedrStatusFilter,
  onChange: (filter: SedrStatusFilter) => void,
): HTMLElement {
  return renderStatusChipGroup({
    className: 'sedr-filters sedr-filters--lifecycle',
    ariaLabelKey: 'sedr.filters.lifecycleGroup',
    chips: [
      ['NotApplicable', 'sedr.status.NotApplicable'],
      ['Superseded', 'sedr.status.Superseded'],
      ['Retired', 'sedr.status.Retired'],
      ['Archived', 'sedr.status.Archived'],
    ],
    active,
    onChange,
  });
}

/** @deprecated Prefer renderComplianceStatusFilters + category select. */
export function renderComplianceFilters(active: string, onChange: (filter: string) => void): HTMLElement {
  const normalized: SedrStatusFilter =
    active === 'all' || !isSedrRequirementStatus(active)
      ? sedrAllActiveStatusFilter()
      : [active];
  return renderComplianceStatusFilters(normalized, (next) => {
    const quick = detectSedrStatusQuickLink(next);
    if (quick === 'allActive' || quick === 'all') onChange('all');
    else if (next.length === 1) onChange(next[0]!);
    else onChange(next[0] ?? 'all');
  });
}

export function renderCompliancePhaseSelect(options: {
  phaseId: string | null | 'unassigned';
  phases: Array<{ id: string; name: string }>;
  onChange: (phaseId: string | null | 'unassigned') => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__phase';
  wrap.innerHTML = `
    <span data-i18n="sedr.phases.field"></span>
    <select id="sedr-compliance-phase"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;
  const allOpt = document.createElement('option');
  allOpt.value = '';
  allOpt.setAttribute('data-i18n', 'sedr.phases.filterAll');
  select.append(allOpt);

  const unassignedOpt = document.createElement('option');
  unassignedOpt.value = 'unassigned';
  unassignedOpt.setAttribute('data-i18n', 'sedr.phases.unassigned');
  select.append(unassignedOpt);

  for (const phase of options.phases) {
    const opt = document.createElement('option');
    opt.value = phase.id;
    opt.textContent = phase.name;
    select.append(opt);
  }

  select.value = options.phaseId ?? '';
  select.addEventListener('change', () => {
    const value = select.value;
    if (value === '') options.onChange(null);
    else if (value === 'unassigned') options.onChange('unassigned');
    else options.onChange(value);
  });

  applyI18n(wrap);
  return wrap;
}

export function renderComplianceOwnerSelect(options: {
  ownerFilter: SedrOwnerFilter;
  owners: string[];
  onChange: (owner: SedrOwnerFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__owner';
  wrap.innerHTML = `
    <span data-i18n="sedr.filters.owner"></span>
    <select id="sedr-compliance-owner"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;

  const allOpt = document.createElement('option');
  allOpt.value = 'all';
  allOpt.setAttribute('data-i18n', 'sedr.filters.all');
  select.append(allOpt);

  const unassignedOpt = document.createElement('option');
  unassignedOpt.value = 'unassigned';
  unassignedOpt.setAttribute('data-i18n', 'sedr.filters.ownerUnassigned');
  select.append(unassignedOpt);

  for (const owner of options.owners) {
    const opt = document.createElement('option');
    opt.value = owner;
    opt.textContent = owner;
    select.append(opt);
  }

  select.value = options.ownerFilter;
  select.addEventListener('change', () => options.onChange(select.value));
  applyI18n(wrap);
  return wrap;
}

export function renderComplianceRenewalSelect(options: {
  renewalFilter: SedrRenewalFilter;
  onChange: (filter: SedrRenewalFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__renewal';
  wrap.innerHTML = `
    <span data-i18n="sedr.filters.renewal"></span>
    <select id="sedr-compliance-renewal"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;
  const choices: Array<[SedrRenewalFilter, string]> = [
    ['all', 'sedr.filters.all'],
    ['renewalPending', 'sedr.filters.renewalPending'],
    ['inGrace', 'sedr.filters.inGrace'],
    ['expiresSoon', 'sedr.filters.expiresSoon'],
    ['expired', 'sedr.filters.expired'],
  ];
  for (const [value, labelKey] of choices) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.setAttribute('data-i18n', labelKey);
    select.append(opt);
  }
  select.value = options.renewalFilter;
  select.addEventListener('change', () => options.onChange(select.value as SedrRenewalFilter));
  applyI18n(wrap);
  return wrap;
}

export function renderComplianceLifecycleRequestSelect(options: {
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  onChange: (filter: SedrLifecycleRequestFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__lifecycle-request';
  wrap.innerHTML = `
    <span data-i18n="sedr.filters.lifecycleRequest"></span>
    <select id="sedr-compliance-lifecycle-request"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;
  const choices: Array<[SedrLifecycleRequestFilter, string]> = [
    ['all', 'sedr.filters.all'],
    ['lifecyclePending', 'sedr.filters.lifecyclePending'],
  ];
  for (const [value, labelKey] of choices) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.setAttribute('data-i18n', labelKey);
    select.append(opt);
  }
  select.value = options.lifecycleRequestFilter;
  select.addEventListener('change', () =>
    options.onChange(select.value as SedrLifecycleRequestFilter),
  );
  applyI18n(wrap);
  return wrap;
}

export function renderComplianceEvidenceSelect(options: {
  evidenceFilter: SedrEvidenceFilter;
  onChange: (filter: SedrEvidenceFilter) => void;
}): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'sedr-compliance-filters__evidence';
  wrap.innerHTML = `
    <span data-i18n="sedr.filters.evidence"></span>
    <select id="sedr-compliance-evidence"></select>
  `;
  const select = wrap.querySelector('select') as HTMLSelectElement;
  const choices: Array<[SedrEvidenceFilter, string]> = [
    ['all', 'sedr.filters.all'],
    ['hasEvidence', 'sedr.filters.hasEvidence'],
    ['noEvidence', 'sedr.filters.noEvidence'],
  ];
  for (const [value, labelKey] of choices) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.setAttribute('data-i18n', labelKey);
    select.append(opt);
  }
  select.value = options.evidenceFilter;
  select.addEventListener('change', () => options.onChange(select.value as SedrEvidenceFilter));
  applyI18n(wrap);
  return wrap;
}

export function renderComplianceFilterBar(options: {
  statusFilter: SedrStatusFilter;
  onStatusChange: (filter: SedrStatusFilter) => void;
  obsoleteSubtypes: readonly SedrRequirementStatus[];
  onObsoleteSubtypesChange: (subtypes: SedrRequirementStatus[]) => void;
  categoryFilter: SedrCategoryFilter;
  categories: SedrCategory[];
  onCategoryChange: (filter: SedrCategoryFilter) => void;
  phaseId: string | null | 'unassigned';
  phases: Array<{ id: string; name: string }>;
  onPhaseChange: (phaseId: string | null | 'unassigned') => void;
  search: string;
  onSearchChange: (search: string) => void;
  commentSearch: string;
  onCommentSearchChange: (commentSearch: string) => void;
  ownerFilter: SedrOwnerFilter;
  owners: string[];
  onOwnerChange: (filter: SedrOwnerFilter) => void;
  renewalFilter: SedrRenewalFilter;
  onRenewalChange: (filter: SedrRenewalFilter) => void;
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  onLifecycleRequestChange: (filter: SedrLifecycleRequestFilter) => void;
  evidenceFilter: SedrEvidenceFilter;
  onEvidenceChange: (filter: SedrEvidenceFilter) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** Mobile filters step: always expanded, no desktop collapse toggle. */
  hideCollapseToggle?: boolean;
}): HTMLElement {
  const hideCollapse = Boolean(options.hideCollapseToggle);
  const collapsed = hideCollapse ? false : options.collapsed;
  const bar = document.createElement('div');
  bar.className = `sedr-compliance-filters${collapsed ? ' sedr-compliance-filters--collapsed' : ''}`;
  bar.id = 'sedr-filter-slot';

  const header = document.createElement('div');
  header.className = 'sedr-compliance-filters__header';

  if (!collapsed) {
    const search = document.createElement('input');
    search.type = 'search';
    search.id = 'sedr-compliance-search';
    search.className = 'sedr-search';
    search.value = options.search;
    search.setAttribute('data-i18n-placeholder', 'sedr.search.requirements');
    search.addEventListener('input', () => options.onSearchChange(search.value));
    header.append(search);

    const commentSearch = document.createElement('input');
    commentSearch.type = 'search';
    commentSearch.id = 'sedr-compliance-comment-search';
    commentSearch.className = 'sedr-search sedr-search--comment';
    commentSearch.value = options.commentSearch;
    commentSearch.setAttribute('data-i18n-placeholder', 'sedr.search.commentContent');
    commentSearch.addEventListener('input', () => options.onCommentSearchChange(commentSearch.value));
    header.append(commentSearch);
  }

  if (collapsed) {
    const summaryParts = listActiveComplianceFilterSummaryParts({
      statusFilter: options.statusFilter,
      categoryFilter: options.categoryFilter,
      categories: options.categories,
      phaseId: options.phaseId,
      phases: options.phases,
      ownerFilter: options.ownerFilter,
      renewalFilter: options.renewalFilter,
      lifecycleRequestFilter: options.lifecycleRequestFilter,
      evidenceFilter: options.evidenceFilter,
      search: options.search,
      commentSearch: options.commentSearch,
    });
    if (summaryParts.length > 0) {
      header.append(renderComplianceFilterSummary(summaryParts));
    }
  }

  if (!hideCollapse) {
    const collapseBtn = document.createElement('button');
    collapseBtn.type = 'button';
    collapseBtn.className = 'help-tip sedr-compliance-filters__toggle';
    collapseBtn.setAttribute(
      'data-i18n-aria-label',
      collapsed ? 'sedr.filters.showListChrome' : 'sedr.filters.hideListChrome',
    );
    collapseBtn.setAttribute(
      'data-i18n-title',
      collapsed ? 'sedr.filters.showListChrome' : 'sedr.filters.hideListChrome',
    );
    collapseBtn.setAttribute(
      'aria-label',
      t(collapsed ? 'sedr.filters.showListChrome' : 'sedr.filters.hideListChrome'),
    );
    collapseBtn.title = t(collapsed ? 'sedr.filters.showListChrome' : 'sedr.filters.hideListChrome');
    collapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    collapseBtn.append(iconImg(collapsed ? 'filter' : 'chevron-up', 14));
    collapseBtn.addEventListener('click', () => options.onCollapsedChange(!options.collapsed));
    header.append(collapseBtn);
  }
  bar.append(header);

  const body = document.createElement('div');
  body.className = 'sedr-compliance-filters__body';
  body.hidden = collapsed;

  const row = document.createElement('div');
  row.className = 'sedr-compliance-filters__row';
  row.append(renderCompliancePhaseSelect({
    phaseId: options.phaseId,
    phases: options.phases,
    onChange: options.onPhaseChange,
  }));
  row.append(renderComplianceCategorySelect({
    categoryFilter: options.categoryFilter,
    categories: options.categories,
    onChange: options.onCategoryChange,
  }));
  row.append(renderComplianceOwnerSelect({
    ownerFilter: options.ownerFilter,
    owners: options.owners,
    onChange: options.onOwnerChange,
  }));
  row.append(renderComplianceRenewalSelect({
    renewalFilter: options.renewalFilter,
    onChange: options.onRenewalChange,
  }));
  row.append(renderComplianceLifecycleRequestSelect({
    lifecycleRequestFilter: options.lifecycleRequestFilter,
    onChange: options.onLifecycleRequestChange,
  }));
  row.append(renderComplianceEvidenceSelect({
    evidenceFilter: options.evidenceFilter,
    onChange: options.onEvidenceChange,
  }));
  body.append(row);

  const statusRow = document.createElement('div');
  statusRow.className = 'sedr-compliance-filters__status-row';
  const statusLabel = document.createElement('span');
  statusLabel.className = 'sedr-compliance-filters__row-label';
  statusLabel.setAttribute('data-i18n', 'sedr.filters.status');
  statusRow.append(statusLabel);
  statusRow.append(
    renderComplianceStatusFilters(options.statusFilter, options.onStatusChange, {
      obsoleteSubtypes: options.obsoleteSubtypes,
      onObsoleteSubtypesChange: options.onObsoleteSubtypesChange,
    }),
  );
  body.append(statusRow);

  bar.append(body);
  applyI18n(bar);
  return bar;
}

export function matchesRenewalFilter(
  req: SedrRequirement,
  filter: SedrRenewalFilter,
  now = Date.now(),
): boolean {
  if (filter === 'all') return true;
  if (filter === 'renewalPending') return Boolean(req.renewalStatus);
  if (filter === 'inGrace') return Boolean(req.inApprovalGrace);
  const expiresAt = req.approvalExpiresAt ? Date.parse(req.approvalExpiresAt) : NaN;
  if (!Number.isFinite(expiresAt)) return false;
  if (filter === 'expired') return expiresAt < now;
  if (filter === 'expiresSoon') {
    const soon = now + SEDR_EXPIRY_SOON_DAYS * 24 * 60 * 60 * 1000;
    return expiresAt >= now && expiresAt <= soon;
  }
  return true;
}

export function matchesLifecycleRequestFilter(
  req: SedrRequirement,
  filter: SedrLifecycleRequestFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'lifecyclePending') return Boolean(req.requestedLifecycleStatus);
  return true;
}

export function applyRequirementFilters(
  items: SedrRequirement[],
  statusFilter: SedrStatusFilter,
  categoryFilter: SedrCategoryFilter,
  hideObsolete = true,
  ownerFilter: SedrOwnerFilter = 'all',
  renewalFilter: SedrRenewalFilter = 'all',
  evidenceFilter: SedrEvidenceFilter = 'all',
  lifecycleRequestFilter: SedrLifecycleRequestFilter = 'all',
): SedrRequirement[] {
  let result = items;
  if (statusFilter.length > 0) {
    const allowed = new Set<string>(statusFilter);
    result = result.filter((r) => allowed.has(r.status));
  } else {
    // Empty selection = None (show no requirements). Legacy callers that passed []
    // with hideObsolete for “all active” should use sedrAllActiveStatusFilter().
    void hideObsolete;
    result = [];
  }
  if (categoryFilter !== 'all') {
    result = result.filter((r) => r.category === categoryFilter);
  }
  if (ownerFilter === 'unassigned') {
    result = result.filter((r) => !(r.ownerName ?? '').trim());
  } else if (ownerFilter !== 'all') {
    result = result.filter((r) => (r.ownerName ?? '').trim() === ownerFilter);
  }
  if (renewalFilter !== 'all') {
    result = result.filter((r) => matchesRenewalFilter(r, renewalFilter));
  }
  if (evidenceFilter === 'hasEvidence') {
    result = result.filter((r) => requirementHasEvidence(r));
  } else if (evidenceFilter === 'noEvidence') {
    result = result.filter((r) => !requirementHasEvidence(r));
  }
  if (lifecycleRequestFilter !== 'all') {
    result = result.filter((r) => matchesLifecycleRequestFilter(r, lifecycleRequestFilter));
  }
  return result;
}

/**
 * Legacy single-axis filter. Prefer {@link applyRequirementFilters}.
 * Still accepts old category/combo values for callers that have not migrated.
 */
export function filterRequirements(items: SedrRequirement[], filter: string): SedrRequirement[] {
  switch (filter) {
    case 'PendingUpload':
    case 'UnderReview':
    case 'ApprovedDischarged':
    case 'Denied':
      return applyRequirementFilters(items, [filter], 'all');
    case 'StaticGovernance':
      return applyRequirementFilters(items, sedrAllStatusFilter(), 'StaticGovernance');
    case 'TechnicalTest':
      return applyRequirementFilters(items, sedrAllStatusFilter(), 'TechnicalTest');
    case 'technicalPending':
      return items.filter((r) => r.category === 'TechnicalTest' && r.status !== 'ApprovedDischarged');
    default:
      return items;
  }
}

export function searchRequirements(items: SedrRequirement[], query: string): SedrRequirement[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((r) =>
    r.requirementCode.toLowerCase().includes(q)
    || r.title.toLowerCase().includes(q)
    || (r.description ?? '').toLowerCase().includes(q)
    || (r.ownerName ?? '').toLowerCase().includes(q));
}

export function appendSedrFileTable(
  container: HTMLElement,
  files: Array<{
    id: string;
    name: string;
    isDirectory: boolean;
    blobId: string | null;
    contentType: string | null;
    sizeBytes: number | null;
    createdAt: string;
  }>,
  formatSize: (bytes: number) => string,
  onOpen?: (file: {
    id: string;
    name: string;
    blobId: string | null;
    contentType: string | null;
  }) => void,
): void {
  const fileItems = files.filter((f) => !f.isDirectory);
  container.innerHTML = `
    <table class="data-table sedr-files-table">
      <thead>
        <tr>
          <th data-i18n="sedr.evidence.colName"></th>
          <th data-i18n="sedr.evidence.colType"></th>
          <th data-i18n="sedr.evidence.colSize"></th>
          <th data-i18n="sedr.evidence.colUploaded"></th>
        </tr>
      </thead>
      <tbody></tbody>
    </table>
  `;
  applyI18n(container);
  const tbody = container.querySelector('tbody') as HTMLTableSectionElement;

  if (fileItems.length === 0) {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td colspan="4" class="muted" data-i18n="sedr.evidence.empty"></td>`;
    tbody.append(tr);
    applyI18n(tr);
    return;
  }

  for (const file of fileItems) {
    const tr = document.createElement('tr');
    const nameCell = document.createElement('td');
    const icon = fileTypeImg(fileTypeIconFor(file.name, file.contentType), 16);
    if (file.blobId && onOpen) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button subtle sedr-file-link';
      btn.append(icon);
      btn.append(document.createTextNode(file.name));
      btn.addEventListener('click', () => onOpen(file));
      nameCell.append(btn);
    } else {
      nameCell.append(icon, document.createTextNode(file.name));
    }
    tr.append(nameCell);

    const typeCell = document.createElement('td');
    typeCell.textContent = t(fileTypeLabelKeyFor(file.name, file.contentType));
    tr.append(typeCell);

    const sizeCell = document.createElement('td');
    sizeCell.textContent = file.sizeBytes != null ? formatSize(file.sizeBytes) : '—';
    tr.append(sizeCell);

    const uploadedCell = document.createElement('td');
    uploadedCell.textContent = formatSedrDate(file.createdAt);
    tr.append(uploadedCell);

    tbody.append(tr);
  }
}
